/**
 * C1 Onboarding Service
 * Handles complete C1 organization onboarding flow:
 * - Account creation
 * - User provisioning
 * - Subscription setup
 * - Welcome email with login credentials
 * 
 * CONFIGURATION:
 * All API endpoints are configurable via environment variables:
 * - VITE_C1_API_URL: Backend API for organization/user management
 * - VITE_ONBOARDING_API_URL: Onboarding-specific API endpoint
 * - VITE_URL: Frontend application URL for email links
 * 
 * The backend service handles:
 * - User creation in Cognito
 * - Organization/subscription records in database
 * - Email sending via configured provider (SES, SendGrid, etc.)
 * 
 * This frontend service only sends requests to the backend - 
 * all sensitive operations (email, password generation) happen server-side.
 */

import {
  C1OnboardingRequest,
  C1OnboardingResponse,
  C1OnboardingError,
  SendEmailResponse,
  ApiResponse,
} from '../types/c1-onboarding.types';
import { logger } from '../utils/logger';
import { getRuntimeConfig, getPortalUrl } from '../config/runtime';

// ─── API Configuration ──────────────────────────────────────────────────────
// All endpoints are configurable via runtime config.json first, then env vars
// Backend handles all sensitive operations (email, credentials, etc.)

function getC1ApiUrl(): string {
  const runtimeConfig = getRuntimeConfig();
  return runtimeConfig?.VITE_C1_API_URL ||
    (import.meta.env.VITE_C1_API_URL as string);
}

function getOnboardingApiUrl(): string {
  const runtimeConfig = getRuntimeConfig();
  return runtimeConfig?.VITE_ONBOARDING_API_URL ||
    (import.meta.env.VITE_ONBOARDING_API_URL as string) ||
    getC1ApiUrl();
}

function getAppUrl(): string {
  const runtimeConfig = getRuntimeConfig();
  return runtimeConfig?.VITE_DOMAIN ||
    (import.meta.env.VITE_URL as string) ||
    window.location.origin;
}

// Feature flags
// Only use mock mode if explicitly set - NOT based on NODE_ENV
const MOCK_MODE = import.meta.env.VITE_ONBOARDING_MOCK === 'true';

// ─── Helper Functions ───────────────────────────────────────────────────────

/**
 * Generate a unique ID (simplified UUID v4)
 */
function generateId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Make authenticated API request
 */
async function apiRequest<T>(
  url: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  body?: Record<string, any>,
  headers?: Record<string, string>
): Promise<ApiResponse<T>> {
  const startTime = Date.now();
  
  try {
    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      logger.error('API request failed', { url, status: response.status, data });
      return {
        success: false,
        error: {
          code: data.code || 'API_ERROR',
          message: data.message || 'Request failed',
          details: data.details,
        },
        meta: {
          requestId: data.requestId || generateId(),
          timestamp: new Date().toISOString(),
          duration: Date.now() - startTime,
        },
      };
    }
    
    return {
      success: true,
      data: data as T,
      meta: {
        requestId: data.requestId || generateId(),
        timestamp: new Date().toISOString(),
        duration: Date.now() - startTime,
      },
    };
  } catch (error) {
    logger.error('API request exception', { url, error });
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: error instanceof Error ? error.message : 'Network request failed',
      },
      meta: {
        requestId: generateId(),
        timestamp: new Date().toISOString(),
        duration: Date.now() - startTime,
      },
    };
  }
}

// ─── Core Onboarding Functions ──────────────────────────────────────────────

/**
 * Complete C1 onboarding flow via backend API
 * 
 * The backend handles ALL sensitive operations:
 * - Creating organization in database
 * - Creating user in Cognito with temporary password
 * - Setting up subscription in Stripe
 * - Sending welcome email via configured email provider
 * 
 * This function simply sends the request and receives the result.
 * In mock mode (development), it simulates the flow locally.
 */
export async function onboardC1Organization(
  request: C1OnboardingRequest
): Promise<C1OnboardingResponse | C1OnboardingError> {
  return callBackendOnboardingAPI(request);
}

/**
 * Production: Call backend onboarding API
 * Backend handles: user creation, email sending, password generation
 * Passes session_token header so backend can move signed docs from temp → org folder
 */
async function callBackendOnboardingAPI(
  request: C1OnboardingRequest
): Promise<C1OnboardingResponse | C1OnboardingError> {
  try {
    // Retrieve session token for the registration session
    const sessionToken = typeof localStorage !== 'undefined'
      ? localStorage.getItem('reg_session_token') || ''
      : '';

    const headers: Record<string, string> = {};
    if (sessionToken) {
      headers['X-Registration-Session'] = sessionToken;
    }

    // Java Lambda returns a flat object on success:
    // { organizationId, userId, loginId, tempPassword, c1Identity, email, creditsIncluded }
    const response = await apiRequest<Record<string, any>>(
      `${getOnboardingApiUrl()}/onboarding/complete`,
      'POST',
      {
        ...request,
        appUrl: getAppUrl(), // Backend uses this for email links
      },
      headers
    );
    
    if (response.success && response.data) {
      const d = response.data;

      // If the response already has the expected shape (credentials object), pass through
      if (d.credentials && d.credentials.email) {
        return d as unknown as C1OnboardingResponse;
      }

      return {
        success: true,
        organizationId: d.organizationId || '',
        userId: d.userId || d.c1Identity || '',
        subscriptionId: d.subscriptionId || '',
        credentials: {
          loginId: d.loginId || '',
          email: d.email || request.primaryUser.email,
          temporaryPassword: d.tempPassword || d.temporaryPassword || '',
          requirePasswordChange: true,
        },
        onboardingStatus: 'completed',
        accountStatus: 'active',
        welcomeEmailSent: true,
        loginUrl: d.loginUrl || `${getPortalUrl()}/login`,
        dashboardUrl: d.dashboardUrl || `${getPortalUrl()}/dashboard`,
        supportEmail: d.supportEmail || 'support@arcaml.com',
      };
    }
    
    // If backend API fails, return error (don't fall back to mock in production)
    return {
      success: false,
      error: {
        code: response.error?.code || 'BACKEND_ERROR',
        message: response.error?.message || 'Onboarding service unavailable',
        details: response.error?.details,
      },
    };
  } catch (error) {
    logger.error('Backend onboarding API failed', { error });
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: error instanceof Error ? error.message : 'Failed to connect to onboarding service',
      },
    };
  }
}

// ─── Internal Service Functions ─────────────────────────────────────────────

interface CreateOrgResult {
  success: boolean;
  organizationId: string;
  error?: string;
}
interface CreateSubResult {
  success: boolean;
  subscriptionId: string;
  error?: string;
}
/**
 * Send password reset email
 * Note: In production, this should go through the backend.
 * This function is provided for cases where frontend needs to trigger a reset.
 */
export async function sendPasswordResetEmail(
  email: string,
  resetToken: string,
  expiresInMinutes: number = 60
): Promise<SendEmailResponse> {
  if (!MOCK_MODE) {
    // In production, call backend API
    try {
      const response = await apiRequest<SendEmailResponse>(
        `${getOnboardingApiUrl()}/password-reset`,
        'POST',
        { email, resetToken, expiresInMinutes }
      );
      
      if (response.success && response.data) {
        return response.data;
      }
      
      return {
        success: false,
        error: response.error?.message || 'Failed to send password reset email',
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }
  
  // Mock mode
  const resetUrl = `${getAppUrl()}/reset-password?token=${resetToken}`;
  
  return {
    success: true,
    messageId: `mock-${generateId()}`,
    sentAt: new Date().toISOString(),
  };
}

/**
 * Send subscription confirmation email
 * Note: In production, this should go through the backend.
 */
export async function sendSubscriptionConfirmationEmail(
  email: string,
  name: string,
  subscriptionDetails: {
    planName: string;
    amount: number;
    interval: string;
    nextBillingDate: string;
  }
): Promise<SendEmailResponse> {
  if (!MOCK_MODE) {
    // In production, call backend API
    try {
      const response = await apiRequest<SendEmailResponse>(
        `${getOnboardingApiUrl()}/subscription-confirmation`,
        'POST',
        { email, name, ...subscriptionDetails }
      );
      
      if (response.success && response.data) {
        return response.data;
      }
      
      return {
        success: false,
        error: response.error?.message || 'Failed to send confirmation email',
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }
  
  // Mock mode
  
  return {
    success: true,
    messageId: `mock-${generateId()}`,
    sentAt: new Date().toISOString(),
  };
}

// ─── Audit Logging ──────────────────────────────────────────────────────────

// ─── Validation Functions ───────────────────────────────────────────────────

/**
 * Validate onboarding request before submission
 */
export function validateOnboardingRequest(
  request: Partial<C1OnboardingRequest>
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  // Organization validation
  if (!request.organization?.partyName) {
    errors.push('Organization name is required');
  }
  if (!request.organization?.partyAbn) {
    errors.push('ABN is required');
  }
  if (!request.organization?.contactEmail) {
    errors.push('Contact email is required');
  }
  
  // User validation
  if (!request.primaryUser?.email) {
    errors.push('Primary user email is required');
  }
  if (!request.primaryUser?.firstName) {
    errors.push('Primary user first name is required');
  }
  if (!request.primaryUser?.lastName) {
    errors.push('Primary user last name is required');
  }
  
  // Subscription validation
  if (!request.subscription?.planType) {
    errors.push('Subscription plan is required');
  }
  
  // Agreement validation
  if (!request.termsAccepted) {
    errors.push('Terms and conditions must be accepted');
  }
  if (!request.privacyAccepted) {
    errors.push('Privacy policy must be accepted');
  }
  
  return {
    valid: errors.length === 0,
    errors,
  };
}

// ─── Status Check Functions ─────────────────────────────────────────────────

/**
 * Check onboarding status for an organization
 */
export async function getOnboardingStatus(
  organizationId: string
): Promise<{
  status: string;
  completedSteps: string[];
  pendingSteps: string[];
  errors: string[];
}> {
  try {
    const response = await apiRequest<{
      status: string;
      completedSteps: string[];
      pendingSteps: string[];
      errors: string[];
    }>(
      `${getC1ApiUrl()}/organizations/${organizationId}/onboarding-status`,
      'GET'
    );
    
    if (response.success && response.data) {
      return response.data;
    }
    
    // Mock response
    return {
      status: 'completed',
      completedSteps: ['organization_created', 'user_created', 'subscription_active', 'welcome_email_sent'],
      pendingSteps: [],
      errors: [],
    };
  } catch (error) {
    return {
      status: 'unknown',
      completedSteps: [],
      pendingSteps: [],
      errors: [error instanceof Error ? error.message : 'Unknown error'],
    };
  }
}

/**
 * Check if an email is already registered with an existing organization
 */
export async function checkEmailExists(email: string): Promise<{ exists: boolean; message?: string }> {
  try {
    const response = await fetch(`${getOnboardingApiUrl()}/onboarding/check-email/${encodeURIComponent(email)}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) return { exists: false };
    return response.json();
  } catch {
    return { exists: false };
  }
}
